import React, { useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { CheckCircle2, ArrowLeft, MailCheck } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setMessage(data.message || "Check your inbox for a reset link.");
      setSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen w-screen flex items-center justify-center bg-slate-50 p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-8 login-fade-in">
        <div className="flex items-center gap-3 mb-6" data-testid="forgot-brand">
          <img src="/brand/icon-192.png" alt="Ayobell" className="w-11 h-11 object-contain" />
          <div>
            <div className="font-heading font-bold tracking-[0.14em] text-slate-900 text-[20px] leading-none">AYOBELL</div>
            <div className="text-[10px] text-slate-500 tracking-[0.18em] uppercase mt-1">Care Management Platform</div>
          </div>
        </div>

        {sent ? (
          <div data-testid="forgot-success" className="text-center space-y-4">
            <div className="mx-auto w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center">
              <MailCheck className="w-6 h-6 text-emerald-700" />
            </div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Check your inbox</h2>
            <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
            <div className="pt-2">
              <Link
                to="/login"
                data-testid="btn-back-to-login"
                className="inline-flex items-center gap-2 text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4" /> Back to sign in
              </Link>
            </div>
          </div>
        ) : (
          <>
            <h2 className="font-heading text-2xl font-semibold text-slate-900 mb-1">Forgot your password?</h2>
            <p className="text-sm text-slate-500 mb-6">
              Enter the email on your Ayobell Care account and we'll send you a secure link to choose a new password.
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email" className="text-[11px] font-semibold text-slate-700 uppercase tracking-widest">
                  Work email
                </Label>
                <Input
                  id="email" data-testid="forgot-email"
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  required autoComplete="email"
                  placeholder="you@care-provider.co.uk"
                  className="mt-2 h-11 login-input"
                />
              </div>
              {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 p-3 rounded" data-testid="forgot-error">{error}</div>}
              <Button
                type="submit" disabled={loading || !email} data-testid="forgot-submit"
                className="w-full h-11 bg-[#0F172A] hover:bg-[#1e293b] transition-all duration-200 hover:-translate-y-[1px] hover:shadow-lg"
              >
                {loading ? "Sending link…" : "Send reset link"}
              </Button>
            </form>

            <div className="mt-6 flex items-center justify-between text-xs">
              <Link to="/login" data-testid="btn-back-login" className="inline-flex items-center gap-1.5 text-slate-500 hover:text-slate-900">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to sign in
              </Link>
              <span className="inline-flex items-center gap-1.5 text-slate-500">
                <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Link expires in 60 min
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
